import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import {
	API_KEY_LIMIT,
	formatFileSize,
	INTAKE_STORAGE_LIMIT_LABEL,
	type RevolutConnectionStatus,
} from "@ravnar/api-contract";

import { apiKeysQueryOptions } from "../../api/api-keys";
import { revolutConnectionQueryOptions } from "../../api/revolut";
import { sessionQueryOptions, useSession } from "../../api/session";
import {
	auditLogQueryOptions,
	companyIdentityQueryOptions,
	organizationLimitsQueryOptions,
	organizationSecurityQueryOptions,
	teamMembersQueryOptions,
} from "../../api/settings";
import { SectionRowLink } from "../../components/links";
import { PageHeader } from "../../components/shell/app-shell";
import { Card, CardHeader } from "../../components/ui/card";
import { isNearLimit, usedPercent } from "../../domain/limits";
import { REQUIRED_IDENTITY_FIELDS } from "../../domain/settings";

export const Route = createFileRoute("/settings/")({
	// Prefetch through the Query cache so the page can use useSuspenseQuery without a loading flash.
	loader: async ({ context }) => {
		const session = await context.queryClient.query(sessionQueryOptions());
		await Promise.all([
			context.queryClient.query({ ...companyIdentityQueryOptions(), staleTime: "static" }),
			context.queryClient.query({ ...teamMembersQueryOptions(), staleTime: "static" }),
			context.queryClient.query({ ...organizationLimitsQueryOptions(), staleTime: "static" }),
			// Only owners and admins may read the audit log or the security rules.
			session?.member.role === "member"
				? undefined
				: context.queryClient.query({ ...auditLogQueryOptions(), staleTime: "static" }),
			session?.member.role === "member"
				? undefined
				: context.queryClient.query({ ...organizationSecurityQueryOptions(), staleTime: "static" }),
			// Only owners and admins may read the Revolut connection or the API keys.
			session?.member.role === "member"
				? undefined
				: context.queryClient.query({ ...revolutConnectionQueryOptions(), staleTime: "static" }),
			session?.member.role === "member"
				? undefined
				: context.queryClient.query({ ...apiKeysQueryOptions(), staleTime: "static" }),
		]);
	},
	head: () => ({ meta: [{ title: "Settings · Ravnar" }] }),
	staticData: { crumbs: ["Settings"] },
	component: SettingsOverviewPage,
});

function plural(count: number, singular: string, pluralForm: string): string {
	return `${count} ${count === 1 ? singular : pluralForm}`;
}

function SettingsOverviewPage() {
	const { member } = useSession();
	const { data: identity } = useSuspenseQuery(companyIdentityQueryOptions());
	const { data: members } = useSuspenseQuery(teamMembersQueryOptions());
	const missingFields = REQUIRED_IDENTITY_FIELDS.filter(field => identity[field].trim() === "").length;

	return (
		<>
			<PageHeader
				title="Settings"
				description="Company details, your account, who has access, and a record of everything that happens."
			/>
			<Card>
				<CardHeader title="All settings" />
				<SectionRowLink
					to="/settings/identity"
					name="Identity"
					status={missingFields === 0 ? "ok" : "attention"}
					statusText={
						missingFields === 0
							? "Complete"
							: `${plural(missingFields, "required field", "required fields")} to fill in`
					}
				/>
				<SectionRowLink
					to="/settings/profile"
					name="Profile"
					status="ok"
					statusText={`Signed in as ${member.email}`}
				/>
				<SectionRowLink
					to="/settings/team"
					name="Team & Access"
					status="ok"
					statusText={plural(members.length, "member", "members")}
				/>
				<LimitsRow />
				{member.role === "member" ? null : <SecurityRow />}
				{member.role === "member" ? null : <AuditLogRow />}
				{member.role === "member" ? null : <ApiKeysRow />}
				{member.role === "member" ? null : <RevolutRow />}
			</Card>
		</>
	);
}

/**
 * What the organization's documents take of its storage and how much of this month's AI tokens its agents have spent,
 * marked for attention once either is four fifths used.
 */
function LimitsRow() {
	const { data: limits } = useSuspenseQuery(organizationLimitsQueryOptions());
	const { storage, aiTokens } = limits;
	const near = isNearLimit(storage.used, storage.limit) || isNearLimit(aiTokens.used, aiTokens.limit);
	return (
		<SectionRowLink
			to="/settings/limits"
			name="Limits"
			status={near ? "attention" : "ok"}
			statusText={`${formatFileSize(storage.used)} of ${INTAKE_STORAGE_LIMIT_LABEL} · ${String(usedPercent(aiTokens.used, aiTokens.limit))}% of this month's AI tokens`}
		/>
	);
}

/**
 * A component of its own, so members, who may not read the organization's security rules, never ask the API for them.
 */
function SecurityRow() {
	const { data: security } = useSuspenseQuery(organizationSecurityQueryOptions());
	return (
		<SectionRowLink
			to="/settings/security"
			name="Security"
			status={security.requireMfa ? "ok" : "idle"}
			statusText={security.requireMfa ? "Required for everyone" : "Optional"}
		/>
	);
}

/**
 * A component of its own, so members, who may not read the audit log, never ask the API for it. The log comes a page at
 * a time and has no count, so the row names its newest entry; the Audit log page shows the same newest page.
 */
function AuditLogRow() {
	const { data: auditLog } = useSuspenseQuery(auditLogQueryOptions());
	const latest = auditLog.entries.at(0);
	return (
		<SectionRowLink
			to="/settings/audit-log"
			name="Audit log"
			status="idle"
			statusText={latest === undefined ? "No entries yet" : `Latest: ${latest.action}`}
		/>
	);
}

/** A component of its own, so members, who may not read the API keys, never ask the API for them. */
function ApiKeysRow() {
	const { data } = useSuspenseQuery(apiKeysQueryOptions());
	const count = data.keys.length;
	return (
		<SectionRowLink
			to="/settings/api-keys"
			name="API keys"
			status={count === 0 ? "idle" : "ok"}
			statusText={count === 0 ? "None yet" : `${String(count)} of ${String(API_KEY_LIMIT)} keys`}
		/>
	);
}

const REVOLUT_ROW: Readonly<
	Record<RevolutConnectionStatus, { status: "ok" | "attention" | "idle"; statusText: string }>
> = {
	connected: { status: "ok", statusText: "Connected" },
	reconnect: { status: "attention", statusText: "Needs reconnecting" },
	"not-connected": { status: "idle", statusText: "Not connected" },
	unavailable: { status: "idle", statusText: "Not available" },
};

/**
 * A component of its own, so members, who may not read the Revolut connection, never ask the API for it. It reads the
 * connection alone, never the accounts, so opening the settings never asks Revolut anything.
 */
function RevolutRow() {
	const { data: connection } = useSuspenseQuery(revolutConnectionQueryOptions());
	return <SectionRowLink to="/settings/revolut" name="Revolut" {...REVOLUT_ROW[connection.status]} />;
}
